import React, { useState } from 'react';
import { 
  Search, 
  Filter, 
  ChevronDown, 
  MoreVertical,
  User,
  Calendar,
  ShoppingBag,
  Ban,
  CheckCircle,
  Shield,
  UserX,
  UserCheck,
  Loader2
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { cn } from '../../lib/utils';
import { Timestamp } from 'firebase/firestore';
import { useAdminUsers, setUserStatus, type AdminUser } from '../../hooks/useAdminUsers';

const roleColors: Record<string, { bg: string; text: string }> = {
  shopper: { bg: 'bg-purple-100', text: 'text-purple-700' },
  business: { bg: 'bg-blue-100', text: 'text-blue-700' },
  driver: { bg: 'bg-green-100', text: 'text-green-700' },
  admin: { bg: 'bg-amber-100', text: 'text-amber-700' },
};

const statusColors: Record<string, { bg: string; text: string; icon: React.ElementType }> = {
  active: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
  suspended: { bg: 'bg-red-100', text: 'text-red-700', icon: Ban },
};

function toDate(val: Timestamp | Date | undefined | null): Date {
  if (!val) return new Date();
  if (val instanceof Timestamp) return val.toDate();
  if (val instanceof Date) return val;
  return new Date(val as any);
}

interface UserDetailPanelProps {
  user: AdminUser;
  onClose: () => void;
  onSuspend: () => void;
  onActivate: () => void;
  actionLoading: boolean;
}

function UserDetailPanel({ user, onClose, onSuspend, onActivate, actionLoading }: UserDetailPanelProps) {
  const displayName = user.displayName || user.email;
  const roleStyle = roleColors[user.role];
  const statusStyle = statusColors[user.status] || statusColors.active;
  const StatusIcon = statusStyle.icon;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-lg max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-4 sm:px-6 py-4 sm:py-5 bg-gradient-to-br from-purple-500 to-indigo-600 text-white">
          <div className="flex items-center space-x-3 sm:space-x-4">
            <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-white/20 flex items-center justify-center text-xl sm:text-2xl font-bold backdrop-blur-sm flex-shrink-0">
              {displayName.charAt(0)}
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-lg sm:text-xl font-semibold truncate">{displayName}</h2>
              <p className="text-white/80 text-sm sm:text-base truncate">{user.email}</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 space-y-4 sm:space-y-6 overflow-y-auto flex-1">
          {/* Status & Role */}
          <div className="flex items-center flex-wrap gap-2 sm:gap-3">
            <span className={cn("px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs sm:text-sm font-medium flex items-center space-x-1", statusStyle.bg, statusStyle.text)}>
              <StatusIcon className="h-3 w-3 sm:h-4 sm:w-4" />
              <span className="capitalize">{user.status}</span>
            </span>
            <span className={cn("px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs sm:text-sm font-medium capitalize", roleStyle.bg, roleStyle.text)}>
              {user.role}
            </span>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            <div className="bg-gray-50 rounded-lg sm:rounded-xl p-3 sm:p-4">
              <div className="flex items-center space-x-2 text-body">
                <ShoppingBag className="h-3 w-3 sm:h-4 sm:w-4" />
                <span className="text-xs sm:text-sm">Total Orders</span>
              </div>
              <p className="text-xl sm:text-2xl font-bold text-heading mt-1">{user.ordersCount || 0}</p>
            </div>
            <div className="bg-gray-50 rounded-lg sm:rounded-xl p-3 sm:p-4">
              <div className="flex items-center space-x-2 text-body">
                <span className="text-xs sm:text-sm">💰</span>
                <span className="text-xs sm:text-sm">Total Spent</span>
              </div>
              <p className="text-xl sm:text-2xl font-bold text-heading mt-1">${(user.totalSpent || 0).toLocaleString()}</p>
            </div>
          </div>

          {/* Details */}
          <div className="space-y-2 sm:space-y-3">
            <div className="flex items-center justify-between py-2 border-b border-gray-100 gap-2">
              <span className="text-body flex items-center space-x-2 text-xs sm:text-sm">
                <Calendar className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                <span>Member Since</span>
              </span>
              <span className="text-heading font-medium text-xs sm:text-sm">{toDate(user.createdAt).toLocaleDateString()}</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-gray-100 gap-2">
              <span className="text-body flex items-center space-x-2 text-xs sm:text-sm">
                <User className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                <span>Last Active</span>
              </span>
              <span className="text-heading font-medium text-xs sm:text-sm truncate max-w-[120px]">{toDate(user.lastActive).toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-gray-100 gap-2">
              <span className="text-body flex items-center space-x-2 text-xs sm:text-sm">
                <Shield className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                <span>User ID</span>
              </span>
              <span className="text-heading font-mono text-[10px] sm:text-sm truncate max-w-[100px]">{user.id}</span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 pt-2 sm:pt-4">
            <Button variant="outline" onClick={onClose} disabled={actionLoading} className="flex-1 h-10 sm:h-auto text-sm">
              Close
            </Button>
            {user.status === 'suspended' ? (
              <Button 
                onClick={onActivate}
                disabled={actionLoading}
                className="flex-1 bg-green-500 hover:bg-green-600 h-10 sm:h-auto text-sm"
              >
                {actionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UserCheck className="h-4 w-4 mr-2" />}
                Activate User
              </Button>
            ) : (
              <Button 
                onClick={onSuspend}
                disabled={actionLoading}
                className="flex-1 bg-red-500 hover:bg-red-600 h-10 sm:h-auto text-sm"
              >
                {actionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UserX className="h-4 w-4 mr-2" />}
                Suspend User
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function AdminUsers() {
  const { users, loading, error } = useAdminUsers();
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const handleSuspend = async () => {
    if (!selectedUser) return;
    setActionLoading(true);
    setActionError(null);
    try {
      await setUserStatus(selectedUser, true, 'Suspended by admin');
      setSelectedUser(null);
    } catch (err: any) {
      console.error('Failed to suspend user:', err);
      setActionError(err.message || 'Failed to suspend user');
    } finally {
      setActionLoading(false);
    }
  };

  const handleActivate = async () => {
    if (!selectedUser) return;
    setActionLoading(true);
    setActionError(null);
    try {
      await setUserStatus(selectedUser, false);
      setSelectedUser(null);
    } catch (err: any) {
      console.error('Failed to activate user:', err);
      setActionError(err.message || 'Failed to activate user');
    } finally {
      setActionLoading(false);
    }
  };

  const filteredUsers = users.filter(user => {
    const name = (user.displayName || user.email || '').toLowerCase();
    const matchesSearch = 
      name.includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.id.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    const matchesStatus = statusFilter === 'all' || user.status === statusFilter;
    
    return matchesSearch && matchesRole && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Ban className="h-12 w-12 text-red-300 mx-auto mb-4" />
          <p className="text-heading font-medium">Failed to load users</p>
          <p className="text-body text-sm mt-1">{error}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h1 className="text-xl sm:text-2xl font-serif text-heading">Users</h1>
          <p className="text-body text-sm sm:text-base mt-0.5 sm:mt-1">Manage platform users and their accounts</p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="text-xs sm:text-sm text-body">{filteredUsers.length} users</div>
        </div>
      </div>

      {actionError && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {actionError}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {[
          { label: 'Total Users', value: users.length, color: 'bg-purple-500' },
          { label: 'Active', value: users.filter(u => u.status === 'active').length, color: 'bg-green-500' },
          { label: 'Suspended', value: users.filter(u => u.status === 'suspended').length, color: 'bg-red-500' },
          { label: 'Shoppers', value: users.filter(u => u.role === 'shopper').length, color: 'bg-blue-500' },
        ].map((stat) => (
          <Card key={stat.label}>
            <CardContent className="p-3 sm:p-4 flex items-center space-x-3 sm:space-x-4">
              <div className={cn("w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl flex items-center justify-center text-white flex-shrink-0", stat.color)}>
                <User className="h-5 w-5 sm:h-6 sm:w-6" />
              </div>
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-body truncate">{stat.label}</p>
                <p className="text-lg sm:text-2xl font-bold text-heading">{stat.value}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Search & Filters */}
      <Card>
        <CardContent className="p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
              <Input
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 sm:pl-10 text-sm h-9 sm:h-10"
              />
            </div>
            <Button 
              variant="outline" 
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center justify-center space-x-2 h-9 sm:h-10 text-sm"
            >
              <Filter className="h-4 w-4" />
              <span>Filters</span>
              <ChevronDown className={cn("h-4 w-4 transition-transform", showFilters && "rotate-180")} />
            </Button>
          </div>

          {showFilters && (
            <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-100 grid sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-heading mb-1.5 sm:mb-2">Role</label>
                <div className="flex flex-wrap gap-1.5 sm:gap-2">
                  {['all', 'shopper', 'business', 'driver', 'admin'].map((role) => (
                    <button
                      key={role}
                      onClick={() => setRoleFilter(role)}
                      className={cn(
                        "px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium transition-colors capitalize",
                        roleFilter === role
                          ? "bg-primary text-white"
                          : "bg-gray-100 text-body hover:bg-gray-200"
                      )}
                    >
                      {role}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-heading mb-1.5 sm:mb-2">Status</label>
                <div className="flex flex-wrap gap-1.5 sm:gap-2">
                  {['all', 'active', 'suspended'].map((status) => (
                    <button
                      key={status}
                      onClick={() => setStatusFilter(status)}
                      className={cn(
                        "px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium transition-colors capitalize",
                        statusFilter === status
                          ? "bg-primary text-white"
                          : "bg-gray-100 text-body hover:bg-gray-200"
                      )}
                    >
                      {status}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Users Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        {filteredUsers.map((user) => {
          const displayName = user.displayName || user.email;
          const roleStyle = roleColors[user.role];
          const statusStyle = statusColors[user.status] || statusColors.active;
          const StatusIcon = statusStyle.icon;

          return (
            <Card 
              key={user.id} 
              className="hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => setSelectedUser(user)}
            >
              <CardContent className="p-3 sm:p-4 lg:p-5">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center text-white font-bold text-sm sm:text-lg flex-shrink-0">
                      {displayName.charAt(0)}
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-heading text-sm sm:text-base truncate">{displayName}</p>
                      <p className="text-xs sm:text-sm text-body truncate">{user.email}</p>
                    </div>
                  </div>
                  <button 
                    className="p-1 hover:bg-gray-100 rounded-lg flex-shrink-0"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MoreVertical className="h-4 w-4 text-gray-400" />
                  </button>
                </div>

                <div className="mt-3 sm:mt-4 flex items-center flex-wrap gap-1.5 sm:gap-2">
                  <span className={cn("px-1.5 sm:px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium flex items-center space-x-1", statusStyle.bg, statusStyle.text)}>
                    <StatusIcon className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
                    <span className="capitalize">{user.status}</span>
                  </span>
                  <span className={cn("px-1.5 sm:px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium capitalize", roleStyle.bg, roleStyle.text)}>
                    {user.role}
                  </span>
                </div>

                <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-100 grid grid-cols-2 gap-3 sm:gap-4 text-xs sm:text-sm">
                  <div>
                    <p className="text-body">Orders</p>
                    <p className="font-semibold text-heading">{user.ordersCount || 0}</p>
                  </div>
                  <div>
                    <p className="text-body">Total Spent</p>
                    <p className="font-semibold text-heading">${(user.totalSpent || 0).toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {filteredUsers.length === 0 && (
        <Card>
          <CardContent className="py-8 sm:py-12 text-center">
            <User className="h-10 w-10 sm:h-12 sm:w-12 text-gray-300 mx-auto mb-3 sm:mb-4" />
            <p className="text-heading font-medium text-sm sm:text-base">No users found</p>
            <p className="text-body text-xs sm:text-sm mt-1">Try adjusting your search or filters</p>
          </CardContent>
        </Card>
      )}

      {/* User Detail Modal */}
      {selectedUser && (
        <UserDetailPanel 
          user={selectedUser} 
          onClose={() => { setSelectedUser(null); setActionError(null); }}
          onSuspend={handleSuspend}
          onActivate={handleActivate}
          actionLoading={actionLoading}
        />
      )}
    </div>
  );
}

